/**
 * ProductsReadinessLayer — Plan-012 Master-Detail-Schicht (S6 Redesign-Shell)
 * Kompositon: ReadinessSummaryCards + ReadinessIssuesPanel (bei channelId) +
 *             ProductActionDrawer + AnalyzeImagesModal + PackshotPickerDialog
 * Reine Präsentationsschicht; Daten kommen via Props aus useProductsPageUi.
 */
import { Lightbulb } from 'lucide-react'
import { Button } from '@/components/v2/ui'
import { ReadinessSummaryCards } from '@/components/v2/channel-readiness/ReadinessSummaryCards'
import { ReadinessIssuesPanel } from '@/components/v2/channel-readiness/ReadinessIssuesPanel'
import { ProductActionDrawer } from '@/components/v2/products/drawer'
import { AnalyzeImagesModal } from '@/components/v2/channel-readiness/AnalyzeImagesModal'
import { PackshotPickerDialog } from '@/components/v2/channel-readiness/PackshotPickerDialog'
import type { ReadinessSummaryItem, IssueFilter } from '@/lib/api/channel-readiness'
import type { ProductWithReadiness } from '@/types/products'
import type { ProductMeta } from '@/components/v2/channel-readiness/PackshotPickerDialog'

interface Props {
  summary: ReadinessSummaryItem[]
  selectedChannelId: string | null
  onSelectChannel: (id: string) => void
  channelId: string | null
  issueFilter: IssueFilter | null
  onSelectIssue: (f: IssueFilter | null) => void
  imageStatusFilter: string[]
  onImageStatusFilter: (f: string[]) => void
  quota: { remaining: number; limit: number } | null | undefined
  drawerProduct: ProductWithReadiness | null
  onCloseDrawer: () => void
  analyzeOpen: boolean
  onCloseAnalyze: (open: boolean) => void
  packshotPickerOpen: boolean
  onClosePackshot: (open: boolean) => void
  selectedIdsArray: string[]
  packshotProductMeta: Record<string, ProductMeta>
  onPackshotAnalyzeRequest: () => void
  onAnalyze: () => void
}

export function ProductsReadinessLayer({
  summary, selectedChannelId, onSelectChannel, channelId,
  issueFilter, onSelectIssue, imageStatusFilter, onImageStatusFilter,
  quota, drawerProduct, onCloseDrawer,
  analyzeOpen, onCloseAnalyze, packshotPickerOpen, onClosePackshot,
  selectedIdsArray, packshotProductMeta, onPackshotAnalyzeRequest, onAnalyze,
}: Props) {
  return (
    <>
      {summary.length > 0 && (
        <ReadinessSummaryCards summary={summary} selectedChannelId={selectedChannelId} onSelect={onSelectChannel} />
      )}

      {channelId && (
        <aside data-testid="readiness-sidebar" className="merged-issues-column space-y-3">
          <Button variant="primary" className="w-full gap-2" onClick={onAnalyze}>
            <Lightbulb className="h-4 w-4" aria-hidden="true" />Bilder analysieren
          </Button>
          {quota && (
            <p className="px-1 text-xs text-neutral-500 text-right">
              {quota.remaining} / {quota.limit} Analysen verfügbar
            </p>
          )}
          <ReadinessIssuesPanel
            channelId={channelId} selectedIssue={issueFilter}
            onSelectIssue={onSelectIssue}
            imageStatusFilter={imageStatusFilter}
            onImageStatusFilter={onImageStatusFilter}
          />
        </aside>
      )}

      <ProductActionDrawer
        open={!!drawerProduct} productId={drawerProduct?.id ?? null}
        productName={drawerProduct?.name} productSku={drawerProduct?.sku}
        thumbnailUrl={drawerProduct?.first_image_url} state={drawerProduct?.readiness_action_state}
        onClose={onCloseDrawer}
      />
      <AnalyzeImagesModal
        open={analyzeOpen} onOpenChange={onCloseAnalyze}
        productIds={selectedIdsArray.length > 0 ? selectedIdsArray : undefined}
      />
      <PackshotPickerDialog
        open={packshotPickerOpen} onOpenChange={onClosePackshot}
        productIds={selectedIdsArray} productMeta={packshotProductMeta}
        channelId={channelId ?? undefined}
        onAnalyzeRequest={onPackshotAnalyzeRequest}
      />
    </>
  )
}
